import {type ReactNode, type SyntheticEvent, useMemo} from 'react';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import GridOnIcon from '@mui/icons-material/GridOn';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import Box from '@mui/material/Box';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import {NodeType} from 'crqlar-react-common/dist/models/automation/automation';
import {SurveyStatus} from 'crqlar-react-common/dist/models/survey/survey.model';
import {useOrganization} from 'crqlar-react-common/dist/state/auth.reducer';
import {formatFullDMY} from 'crqlar-react-common/dist/utils/date.util';
import {getIconForLanguage, getLocalizedLangLabel} from 'crqlar-react-common/dist/utils/translations.util';
import {isEqual} from 'lodash';
import {useTranslation} from 'react-i18next';

import {
    MAX_CUSTOM_DAYS,
    MIN_CUSTOM_DAYS,
    REPORT_FORMAT_NAMES,
    REPORT_FORMATS,
    REPORT_LANGUAGES,
    ReportFormat,
    defaultReportLanguage,
    formatReportFormats,
    readCustomDays,
    readReportCompareMode,
    readReportFormats,
    readReportLanguage,
    readReportPeriod,
    reportPeriodLabel,
    reportPeriodPatch,
    reportPeriodSelection,
    reportRunDay
} from '../../../config/surveyReport';
import {surveyTypeLabel} from '../../../config/surveyTriggers';
import {scheduleTimeZone} from '../../../utils/timeZone';
import InsightsDateRangeControl from '../../../../../insights/v2/base/InsightsDateRangeControl';
import {relativeRangeForPreset} from '../../../../../insights/v2/insightsDateRangePresets';
import {MultiSelect, SingleSelect} from '../../../../../common/newSelect';
import type {SelectOption} from '../../../../../common/newSelect';
import NodeEditorLayout, {NodeEditorState} from '../NodeEditorLayout';
import {NodeEditorV2Props} from '../types';

const capitalized = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);

const rangeText = (range: {start: Date; end: Date}) => `${formatFullDMY(range.start)} – ${formatFullDMY(range.end)}`;

/** A language's round 18px flag, as `digitalSigning/LanguageOption` draws it; a code without one shows no image. */
const LanguageFlag = ({code}: {code: string}) => {
    const flag = getIconForLanguage(code);
    if (!flag) return null;

    return (
        <Box
            component={'img'}
            src={flag}
            alt={''}
            onError={(event: SyntheticEvent<HTMLImageElement>) => { event.currentTarget.style.display = 'none'; }}
            sx={{width: 18, height: 18, borderRadius: '50%', objectFit: 'cover', flexShrink: 0}}
        />
    );
};

const FORMAT_ICONS: Record<ReportFormat, ReactNode> = {
    PDF: <PictureAsPdfIcon sx={{fontSize: 18, color: 'text.secondary'}}/>,
    DOCX: <DescriptionOutlinedIcon sx={{fontSize: 18, color: 'text.secondary'}}/>,
    XLSX: <GridOnIcon sx={{fontSize: 18, color: 'text.secondary'}}/>
};

const FORMAT_OPTIONS: SelectOption[] = REPORT_FORMATS.map(format => ({
    value: format,
    label: REPORT_FORMAT_NAMES[format],
    adornment: FORMAT_ICONS[format]
}));

/**
 * SURVEY_REPORT (`config/surveyReport.ts`): one survey, a relative period recomputed on every run, at least one
 * format. The two content switches decide what everyone the Team members step picks receives, whatever their own
 * permissions.
 */
const SurveyReportEditorV2 = ({
    nodeConfig,
    setNodeConfig,
    upstreamNodes = [],
    availableSurveys = [],
    availableSurveysLoading,
    readOnly
}: NodeEditorV2Props) => {
    const {t, i18n} = useTranslation();
    const uiLanguage = i18n.resolvedLanguage || i18n.language || 'en';
    const organization = useOrganization();
    const organizationLanguage = organization?.primaryLanguage?.trim().toLowerCase();
    const zone = scheduleTimeZone(organization);

    const surveyId: string = nodeConfig.surveyId || '';
    const period = readReportPeriod(nodeConfig);
    const customDays = readCustomDays(nodeConfig);
    const compareMode = readReportCompareMode(nodeConfig);
    const formats = readReportFormats(nodeConfig);
    const periodSelection = reportPeriodSelection(nodeConfig);

    // The days the next run covers: counted back from the scheduled trigger's next slot, else from today.
    const scheduleConfig = upstreamNodes.find(node => node.type === NodeType.SCHEDULED_TRIGGER)?.config;
    const runDay = useMemo(() => reportRunDay(scheduleConfig, zone, new Date()), [scheduleConfig, zone]);
    const nextRunRange = relativeRangeForPreset(periodSelection.preset, periodSelection.days, runDay);

    // A manual trigger beside the schedule runs on the day it is pressed, so it can cover other days than the next run.
    const manualTrigger = scheduleConfig ? upstreamNodes.find(node => node.type === NodeType.MANUAL_TRIGGER) : undefined;
    const today = useMemo(() => reportRunDay(undefined, zone, new Date()), [zone]);
    const manualRange = manualTrigger ? relativeRangeForPreset(periodSelection.preset, periodSelection.days, today) : null;
    const nextRunText = nextRunRange ? rangeText(nextRunRange) : '';
    const manualText = manualRange ? rangeText(manualRange) : '';
    const manualLabel = manualTrigger?.label?.trim();

    const write = (patch: Record<string, any>) => setNodeConfig({...nodeConfig, ...patch});

    // Grouped like the survey triggers' picker; anonymous surveys have an evaluation too, drafts may already have answers.
    const surveyOptions: SelectOption[] = useMemo(() => availableSurveys
        .map(survey => ({
            value: survey.id,
            label: survey.status === SurveyStatus.PUBLISHED
                ? (survey.name || survey.id)
                : `${survey.name || survey.id} ${t('(draft)')}`.trim(),
            group: survey.type ? surveyTypeLabel(survey.type, t) : t('Other')
        }))
        .sort((left, right) => left.group.localeCompare(right.group) || left.label.localeCompare(right.label)),
    [availableSurveys, t]);

    // The catalogue wins over the stored snapshot, so a renamed survey reads by its current name.
    const surveyName = availableSurveys.find(survey => survey.id === surveyId)?.name || nodeConfig.surveyName || surveyId;

    const languageOptions: SelectOption[] = REPORT_LANGUAGES.map(code => ({
        value: code,
        label: capitalized(getLocalizedLangLabel(code, uiLanguage)),
        adornment: <LanguageFlag code={code}/>
    }));

    // At least one file: removing the last chip, or unticking its row, changes nothing.
    const changeFormats = (values: string[]) => {
        const next = REPORT_FORMATS.filter(format => values.includes(format));
        if (next.length) write({formats: next});
    };

    const {state, summary}: {state: NodeEditorState; summary: string} = (() => {
        if (!surveyId) {
            return {
                state: 'wontRun',
                summary: surveyOptions.length
                    ? t('No survey picked yet, choose the survey this report is about.')
                    : t('This organization has no survey yet. Create one, then pick it here.')
            };
        }

        if (period === 'CUSTOM_DAYS' && !customDays) {
            return {
                state: 'wontRun',
                summary: t('Enter how many days the report covers, from {{min}} to {{max}}.', {min: MIN_CUSTOM_DAYS, max: MAX_CUSTOM_DAYS})
            };
        }

        // Only a config written outside this dialog gets here; the last file cannot be removed.
        if (!formats.length) {
            return {state: 'wontRun', summary: t('Tick at least one file type for the report.')};
        }

        return {
            state: 'configured',
            summary: [
                t('Builds the report on ‘{{survey}}’ once per run: {{period}}, as {{formats}}.', {
                    survey: surveyName,
                    period: reportPeriodLabel(nodeConfig, t),
                    formats: formatReportFormats(formats, uiLanguage)
                }),
                compareMode === 'PREVIOUS_PERIOD' ? t('Each figure is compared with the previous period.') : '',
                compareMode === 'PREVIOUS_YEAR' ? t('Each figure is compared with the same days a year earlier.') : '',
                nextRunText ? t('The next run covers {{range}} in the organization’s time zone.', {range: nextRunText}) : '',
                manualText && manualText !== nextRunText
                    ? (manualLabel
                        ? t('‘{{trigger}}’ today covers {{range}}.', {trigger: manualLabel, range: manualText})
                        : t('A manual run today covers {{range}}.', {range: manualText}))
                    : ''
            ].filter(Boolean).join(' ')
        };
    })();

    const switchRow = (key: 'includeInsights' | 'includeSensitive', label: string, detail: string) => (
        <FormControlLabel
            sx={{m: 0, alignItems: 'flex-start', gap: 1}}
            control={
                <Switch
                    size={'small'}
                    checked={nodeConfig[key] === true}
                    onChange={event => write({[key]: event.target.checked})}
                    disabled={readOnly}
                />
            }
            // Spans, as a label may only hold phrasing content.
            disableTypography
            label={
                <Stack component={'span'}>
                    <Typography component={'span'} variant={'body1'}>{label}</Typography>
                    <Typography component={'span'} variant={'body2'} color={'text.secondary'}>{detail}</Typography>
                </Stack>
            }
        />
    );

    return (
        <NodeEditorLayout state={state} summary={summary}>
            {/* `loading` stays undefined once settled: only a non-empty list proves a stored survey is gone. */}
            <SingleSelect
                label={t('Survey')}
                value={surveyId}
                options={surveyOptions}
                onChange={value => write({
                    surveyId: value,
                    // Stored beside the id so a card can name the survey without loading the catalogue.
                    surveyName: availableSurveys.find(survey => survey.id === value)?.name
                })}
                unknownLabel={nodeConfig.surveyName}
                loading={availableSurveysLoading || undefined}
                disabled={readOnly}
                disableClearable
            />

            {/* Relative: the step stores the preset, and every run counts its days back from its own date. */}
            <Stack gap={0.75} sx={{minWidth: 0}}>
                <FormLabel sx={{fontSize: '0.875rem', fontWeight: 500}}>{t('Reporting period')}</FormLabel>
                <InsightsDateRangeControl
                    relative={periodSelection}
                    onRelativeChange={next => {
                        if (!isEqual(next, periodSelection)) write(reportPeriodPatch(next));
                    }}
                    referenceDay={runDay}
                    disabled={readOnly}
                />
            </Stack>

            <MultiSelect
                label={t('Files')}
                value={formats}
                options={FORMAT_OPTIONS}
                onChange={changeFormats}
                disabled={readOnly}
                disableClearable
            />

            <SingleSelect
                label={t('Language')}
                // A step without a language shows the one it reports in.
                value={readReportLanguage(nodeConfig) ?? defaultReportLanguage(organizationLanguage)}
                options={languageOptions}
                onChange={value => write({language: value})}
                disabled={readOnly}
                disableClearable
            />

            <Stack gap={1}>
                {switchRow('includeInsights', t('Include insights'), t('Score, health, reach, themes and breakdown'))}
                {switchRow('includeSensitive', t('Include sensitive answers'), t('Answers to questions marked as sensitive'))}
                <FormHelperText sx={{m: 0}}>
                    {t('Everyone selected in Team members receives these sections.')}
                </FormHelperText>
            </Stack>
        </NodeEditorLayout>
    );
};

export default SurveyReportEditorV2;
